import { useState, useEffect, useMemo } from 'react'
import { useMutation } from '@apollo/client'
import { Event, EventType } from '@graphql/__generated/graphql'
import { useAuth } from '@libs/Auth/useAuth'
import { useTheme } from '@libs/useTheme'
import { useRouter } from 'next/router'
import { useOrganisationProfile } from '@libs/useOrganisationProfile'
import { FileInput } from '@components/inputs/FileInput'
import useFileUploader from '@libs/useFileUploader'
import useLSFirst from '@libs/useLSFirst'
import { Button } from '@components/inputs/Button'
import EventsView from '@components/event/EventsView'
import LoadingBar from '@components/base/LoadingBar'
import DashboardLayout from '@layouts/DashboardLayout'
import Text from '@components/base/Text'
import Box from '@components/base/Box'
import OrganizationBannerEditPopup from '@components/inputs/OrganizationBannerEditPopup'
import SearchEvent from '@components/dashboard/SearchEvent'
import StylingModal from '@components/dashboard/StylingModal'
import headerBackground from '@public/header-background.png'
import FooterUnauthenticated from '@components/page/FooterUnauthenticated'
import { UPDATE_ORGANISATION } from '@graphql/organisation/updateOrganisation'
import Link from 'next/link'
import toast, { Toaster } from 'react-hot-toast'
import { GET_DASHBOARD_EVENTS } from '@graphql/events/getDashboardEvents'
import { GET_MY_EVENT_OVERVIEWS } from '@graphql/events/getMyEventOverviews'
import { GET_EVENTS_BY_COMPANY_OVERVIEWS } from '@graphql/events/getEventsByCompanyOverviews'
import { GET_PAST_EVENTS } from '@graphql/events/getPastEvents'
import moment from 'moment'
import { pushDataLayerEvent } from '@components/base/GoogleTags'

export default function OrganizerLaunchPage() {
  const { profile, canEditEvent, getPermission } = useAuth()
  const { theme, setTheme } = useTheme()
  const uploadFile = useFileUploader()
  const [updateOrganisation] = useMutation(UPDATE_ORGANISATION)

  const [organisationBanner, setOrganisationBanner] = useState<
    string | File | undefined
  >()
  const [isReady, setIsReady] = useState<boolean>(false)
  const [term, setTerm] = useState('')
  const [filterTypes] = useState<string[]>([
    'ONLINE',
    'IN_PERSON',
    'FUNDRAISER',
  ])

  const isOrganizer = profile?.groups?.includes('organizer')
  const isAdmin = profile?.groups?.includes('forumm-admin')

  useEffect(() => {
    if (profile?.email) setIsReady(true)
  }, [profile])

  useEffect(() => {
    if (profile?.userId && (isOrganizer || isAdmin))
      pushDataLayerEvent('create_event_dashboard')
  }, [isAdmin, isOrganizer, profile?.userId])

  const company = useMemo(
    () => profile?.university ?? profile?.company ?? '',
    [profile]
  )

  const { loading: dashboardEventsLoading, data: dashboardData } = useLSFirst<{
    getDashboardEvents?: { events?: Event[] }
  }>('dashboard-events', GET_DASHBOARD_EVENTS, {})

  const { loading: companyLoading, data: companyData } = useLSFirst<{
    getEventsByCompany?: Event[]
  }>('company-overview-events', GET_EVENTS_BY_COMPANY_OVERVIEWS, {
    variables: {
      company,
    },
    skip: !company,
  })

  const { loading: myEventsLoading, data: myEventsData } = useLSFirst<{
    getMyEvents?: Event[]
  }>('my-events', GET_MY_EVENT_OVERVIEWS, {})

  const { loading: pastEventsLoading, data: pastEventsData } = useLSFirst<{
    getPastEvents?: Event[]
  }>('past-events', GET_PAST_EVENTS, {})

  // const { loading: allEventsLoading, data: allEventsData } = useLSFirst<{
  //   getAllEvents?: Event[]
  // }>('all-events', GET_ALL_EVENTS_OVERVIEWS, {
  //   skip: !isOrganizer && !isAdmin,

  useEffect(() => {
    if (profile) {
      console.log('Profile: ', profile?.email, '\nCompany: ', company)
    }
  }, [profile])

  const {
    loading: organisationLoading,
    profile: organisationData,
    ready: organisationReady,
    refetchOrganisation,
  } = useOrganisationProfile(company)

  const dashboardEvents = (dashboardData?.getDashboardEvents?.events ??
    []) as Event[]
  const myEvents = (myEventsData?.getMyEvents ?? []) as Event[]
  const pastEvents = (pastEventsData?.getPastEvents ?? [])
    ?.filter((f) => !!f.event?.startDateTime)
    ?.sort((a, b) =>
      moment(a.event?.startDateTime).isBefore(moment(b.event?.startDateTime))
        ? 1
        : -1
    ) as Event[]
  // const allEvents = (allEventsData?.getAllEvents ?? []) as Event[]
  const companyEvents = (companyData?.getEventsByCompany ?? []) as Event[]

  const isPubliclyAccessible = (eventItem: Event) => {
    return (
      !!eventItem?.isPublished && eventItem?.event?.publiclyListed !== false
    )
  }

  const canAccessPremiumEvent = (eventItem: Event) => {
    if (!eventItem.event?.organizationName) return false
    if (eventItem.event.organizationName === profile?.company) return true
    const isMember = getPermission('USER::MEMBERSHIP::*')?.includes(
      eventItem.event?.organizationName
    )
    return isMember
  }

  const publicDashboardEvents = dashboardEvents.filter(
    (eventItem) =>
      isPubliclyAccessible(eventItem) &&
      !eventItem.isPremium &&
      eventItem.event?.eventType !== EventType.Fundraiser
  )

  const premiumDashboardEvents = dashboardEvents.filter(
    (eventItem) =>
      isPubliclyAccessible(eventItem) &&
      !!eventItem.isPremium &&
      canAccessPremiumEvent(eventItem)
  )

  // const organiserEditableEvents = allEvents.filter(canEditEvent)
  //
  // const adminAccessibleEvents = allEvents.filter(isPubliclyAccessible)
  //
  // const adminDraftEvents = allEvents.filter(
  //   (eventItem) => !eventItem.isPublished || !eventItem.isComplete
  // )

  const router = useRouter()

  // NOTE: Handles the toast popup when a user completes the signup to login flow.
  useEffect(() => {
    const existingLogin = new URLSearchParams(document.location.search).has(
      'existinglogin'
    )
    if (existingLogin && isReady) {
      toast(
        `Logged in to your exisiting account, using the provided credentials.`,
        {
          icon: '💬',
          duration: 8000,
        }
      )
    }
  }, [isReady])

  const handleUpdateOrganisation = async ({
    bannerImage,
    headerTextOne,
    headerTextTwo,
  }: {
    bannerImage?: string
    headerTextOne?: string
    headerTextTwo?: string
  }) => {
    const companyName = organisationData?.name ?? profile?.company
    if (companyName) {
      let variables = {
        name: companyName,
      } as {
        name: string
        bannerImage?: string
        headerTextOne?: string
        headerTextTwo?: string
      }
      if (bannerImage) variables.bannerImage = bannerImage
      if (headerTextOne) variables.headerTextOne = headerTextOne
      if (headerTextTwo) variables.headerTextTwo = headerTextTwo
      await updateOrganisation({ variables })
      setTimeout(() => {
        refetchOrganisation()
      }, 500)
    }
  }

  const headerBanner =
    organisationBanner ?? organisationData?.bannerImage ?? headerBackground.src

  if (!profile) {
    return <LoadingBar />
  }
  if (!isReady || organisationLoading) {
    return (
      <Box className="flex-1 overflow-y-scroll h-[calc(100vh)] relative flex items-center flex-col">
        <LoadingBar />
      </Box>
    )
  }

  const isDarkTheme = theme.type === 'DARK'

  const headerTitle = organisationData?.headerTextOne ? (
    organisationData?.headerTextOne
  ) : (
    <>
      Welcome to your <br />
      {company} Space
    </>
  )

  const headerSubTitle = organisationData?.headerTextTwo ? (
    organisationData?.headerTextTwo
  ) : (
    <>
      Here you’ll discover all the events you’ve signed up to along with the
      events you’ve created.
    </>
  )

  const totalAccountEvents = [...dashboardEvents, ...companyEvents].filter(
    canEditEvent
  ).length
  const canCreateEvents = !!isOrganizer || !!isAdmin

  // Sets the theme back to Forumm's when on the dashboard.
  // if (
  //   !!theme &&
  //   !!theme.organisationName &&
  //   theme.organisationName !== 'Forumm'
  // )
  //   setTheme({
  //     ...theme,
  //     organisationName: 'Forumm',
  //   })

  const renderEventSection = ({
    title,
    events,
    loading,
    emptyMessage = 'No events match your search yet.',
    skipSort = false,
  }: {
    title: string
    events: Event[]
    loading?: boolean
    emptyMessage?: string
    skipSort?: boolean
  }) => {
    const visibleEvents = (events ?? []).filter((eventItem) => {
      if (!filterTypes.includes(eventItem.event?.eventType ?? '')) return false
      if (!term) return true
      const searchableText = [
        eventItem.event?.title,
        eventItem.event?.organizationName,
        eventItem.event?.shortDescription,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()

      return searchableText.includes(term.toLowerCase())
    })
    const hasEvents = visibleEvents.length > 0

    return (
      <>
        <Text className="text-2xl font-medium -mb-2 mt-16 pl-5 sm:pl-0 ml-0 flex items-center space-x-2">
          <span>{title}</span>
          <span className="rounded-full bg-white/10 px-2 py-1 text-xs">
            {visibleEvents.length}
          </span>
          {loading && (
            <Box
              className="animate-spin inline-flex rounded-full h-[14px] w-[14px] border-2 mt-1 ml-2"
              style={{ borderTopColor: 'rgb(24, 26, 32)' }}
            ></Box>
          )}
        </Text>
        {hasEvents ? (
          <EventsView
            events={visibleEvents}
            types={filterTypes}
            title=""
            currency={organisationData?.currency ?? 'GBP'}
            skipSort={skipSort}
          />
        ) : (
          <Text className="pt-12 pb-12 text-base text-center">
            {emptyMessage}
          </Text>
        )}
      </>
    )
  }
  return (
    <>
      {/* Google Tag Manager (noscript) */}
      {/* <noscript><iframe src="https://www.googletagmanager.com/ns.html?id=GTM-WJ8MVS9P"
height="0" width="0" style={{ display: 'none', visibility: 'hidden' }}></iframe></noscript> */}
      {/* End Google Tag Manager (noscript) */}
      <Box className="flex flex-1 -mt-[80px] pt-[80px] overflow-y-scroll overflow-x-hidden h-[calc(100vh)] items-center flex-col relative">
        <Box
          className="relative sm:flex justify-center w-full md:min-h-[25rem]"
          style={{
            backgroundImage: `url(${headerBanner})`,
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundColor: theme.backgroundColour,
          }}
        >
          <Box className="lg:min-w-[57rem] z-999 relative w-full flex justify-center items-center ">
            <Box
              show={isOrganizer}
              className="absolute cursor-pointer top-5 right-5 z-10"
            >
              <FileInput
                uploadFile={uploadFile}
                crop={true}
                cropAspectRatio={4}
                cropShape={'rect'}
                value={organisationBanner}
                onChange={(data) => {
                  setOrganisationBanner(data)
                  if (typeof data === 'string' && data.length > 0) {
                    handleUpdateOrganisation({ bannerImage: data })
                  }
                }}
                minified
              />
            </Box>
            <Box
              className={`rounded-2xl shadow-lg md:w-[85%] max-w-[1500px] flex p-10 md:p-6 md:px-8 md:py-4 md:pt-6 md:pb-6 z-1000 animate-text-focus-in ${
                isDarkTheme ? '' : 'border border-forumm-menu-border'
              }`}
              color="backgroundColorBanner"
              style={{
                background: isDarkTheme
                  ? 'rgba(0, 0, 0, 0.7)'
                  : 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(5px)',
                WebkitBackdropFilter: 'blur(5px)',
              }}
            >
              <Box className="px-6 lg:px-0 z-1000 relative w-full">
                {isOrganizer && (
                  <OrganizationBannerEditPopup
                    headerTextOne={organisationData?.headerTextOne}
                    headerTextTwo={organisationData?.headerTextTwo}
                    onSave={(headerTextOne, headerTextTwo) => {
                      handleUpdateOrganisation({
                        headerTextOne,
                        headerTextTwo,
                      })
                    }}
                  />
                )}
                <Text className="text-4xl font-medium pb-4 text-center leading-snug mt-2 px-12 sm:px-0 mb-2">
                  {headerTitle}
                </Text>
                <Box>
                  <Text className="text-base text-center px-12 sm:px-6 animate-text-focus-in">
                    {headerSubTitle}
                  </Text>
                </Box>
                <Box className="flex justify-center">
                  <Button
                    className="mt-4"
                    title="Create Event"
                    onClick={() => {
                      router.push('/create-event')
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* TODO: Removed as requested by Dan, to discuss how to handle the new user flow.  */}
        {/* {profile && !isOrganizer && <BecomeOrganiserBanner />} */}

        {/* <Box className="md:w-[85%] max-w-[1500px] mb-2 mt-8 ml-4 text-xs sm:text-sm">
          <Text className={`${!(eventCounter > 0) && 'opacity-0'}`}>
            Explore <b>{eventCounter}</b>{' '}
            {eventCounter === 1 ? 'event' : 'events'}
          </Text>
        </Box> */}
        <Box className="pl-12 mt-6 sm:pl-0 sm:flex sm:flex-row-reverse justify-between md:w-[85%] max-w-[1500px] px-3 sm:p-0 ">
          <Box className="my-2 sm:my-0 ml-2 sm:ml-0">
            <SearchEvent
              searchTerm={term}
              setSearchTerm={(inputText: string) => setTerm(inputText)}
            />
          </Box>
        </Box>
        <Box className="pl-10 sm:pl-0 sm:flex sm:flex-row-reverse justify-between md:w-[85%] max-w-[1500px] px-3 sm:p-0">
          <Box className="text-white p-2 py-6 pt-0 w-full">
            {renderEventSection({
              title: 'My Events',
              events: myEvents,
              loading: myEventsLoading,
              emptyMessage: 'No registered events match your search.',
            })}

            {renderEventSection({
              title: `${profile.company} Events`,
              events: companyEvents,
              loading: companyLoading,
              emptyMessage: 'No events match your search.',
            })}

            {renderEventSection({
              title: 'Find Public Events',
              events: publicDashboardEvents,
              loading: dashboardEventsLoading,
              emptyMessage: 'No public events match your search.',
            })}

            {renderEventSection({
              title: 'Premium Events',
              events: premiumDashboardEvents,
              loading: dashboardEventsLoading,
              emptyMessage: 'No premium events match your search.',
            })}

            {renderEventSection({
              title: 'Past Events',
              events: pastEvents,
              loading: pastEventsLoading,
              emptyMessage: 'No past events match your search.',
              skipSort: true,
            })}

            {/* {isOrganizer && */}
            {/*   renderEventSection({ */}
            {/*     title: `Manage Events`, */}
            {/*     events: organiserEditableEvents, */}
            {/*     loading: allEventsLoading, */}
            {/*     emptyMessage: 'No editable events match your search.', */}
            {/*   })} */}
            {/**/}
            {/* {isAdmin && */}
            {/*   renderEventSection({ */}
            {/*     title: 'Admin: Public Events', */}
            {/*     events: adminAccessibleEvents, */}
            {/*     loading: allEventsLoading, */}
            {/*     emptyMessage: 'No accessible events match your search.', */}
            {/*   })} */}
            {/**/}
            {/* {isAdmin && */}
            {/*   renderEventSection({ */}
            {/*     title: 'Admin: Drafts Events', */}
            {/*     events: adminDraftEvents, */}
            {/*     loading: allEventsLoading, */}
            {/*     emptyMessage: 'No draft events match your search.', */}
            {/*   })} */}
          </Box>
        </Box>
        {isReady &&
          canCreateEvents &&
          !organisationLoading &&
          !myEventsLoading &&
          !dashboardEventsLoading &&
          totalAccountEvents === 0 && (
            <>
              <Button
                type="secondary"
                size="large"
                title="Create your first event"
                className="mt-10 mb-20"
                onClick={() => router.push('/create-event')}
              ></Button>
            </>
          )}
        {isOrganizer && !organisationLoading && organisationReady && (
          <StylingModal organisationData={organisationData} />
        )}
        <FooterUnauthenticated transparent={false} />
        <Toaster
          toastOptions={{
            className:
              'shadow-lg !rounded-full flex items-center border-2 !bg-forumm-white border-forumm-green !text-forumm-green mt-[80px]',
            duration: 4000,
          }}
        />
      </Box>
    </>
  )
}

OrganizerLaunchPage.Layout = DashboardLayout
